import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, FormikValues } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { StickyCard, StickyCardSkeleton } from 'src/components/StickyCard';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { Assessment } from 'src/models/assessment/_types';
import {
  createAssessment,
  fetchAssessment,
  updateAssessment,
} from 'src/models/assessment/assessment';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { DebouncedFormik } from 'src/shared/Forms/DebouncedFormik';
import ScrollToFirstError from 'src/shared/ScrollToFirstError';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

import { AssessmentCustomize } from './Widgets/AssessmentCustomize';
import { AssessmentDetails } from './Widgets/AssessmentDetails';
import { AssessmentFields } from './Widgets/AssessmentFields';
import { AssessmentPreview } from './Widgets/AssessmentPreview';
import { AssessmentRules } from './Widgets/AssessmentRules';

export interface AssessmentFormValues {
  id: Identifier | null;
  name: string;
  description: string;
  requiredAppText: string;
  scored: boolean;
  scoreFormula: string;
  addClientOnCompletion: boolean;
  defaultLifecycleStageId: number | null;
  unassignAfterCompletion: boolean;
  requireLogin: boolean;
  sendEmailConfirmation: boolean;
  enableAssessementAnswerCheck: boolean;
  makeCompletedPrivate: boolean;
  public: boolean;
  autoAssignWeeks: number | null;
  buttonText: string;
  customCss: string;
  customTemplate: string;
  customResults: string;
  thankYouPage: string;
  fields: Assessment['fields'];
  rules: Assessment['rules'];
}

export const AssessmentNewPage = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessment.new',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { assessmentId } = useParams();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const fromCalendar = searchParams.get('fromCalendar') === 'true';

  const [curTab, setCurTab] = useState('details');
  const [useTemplate, setUseTemplate] = useState(false);
  const [useResults, setUseResults] = useState(false);
  const [useThankYou, setUseThankYou] = useState(false);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const tabs = [
    {
      label: t('tabs.details'),
      value: 'details',
      component: <AssessmentDetails />,
    },
    {
      label: t('tabs.fields'),
      value: 'fields',
      component: <AssessmentFields />,
    },
    { label: t('tabs.rules'), value: 'rules', component: <AssessmentRules /> },
    {
      label: t('tabs.customize'),
      value: 'additional',
      component: (
        <AssessmentCustomize
          useTemplate={useTemplate}
          setUseTemplate={setUseTemplate}
          useResults={useResults}
          setUseResults={setUseResults}
          useThankYou={useThankYou}
          setUseThankYou={setUseThankYou}
        />
      ),
    },
    {
      label: t('tabs.preview'),
      value: 'preview',
      component: <AssessmentPreview />,
    },
  ];

  const { data: assessmentData, isLoading: isLoadingAssessment } = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => fetchAssessment(Number(assessmentId)),
    enabled: !!assessmentId,
  });

  useEffect(() => {
    if (assessmentData) {
      setUseTemplate(!!assessmentData.customTemplate);
      setUseResults(!!assessmentData.customResults);
      setUseThankYou(!!assessmentData.thankYouPage);
    }
  }, [
    assessmentData?.customTemplate,
    assessmentData?.customResults,
    assessmentData?.thankYouPage,
    assessmentData,
  ]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (values: AssessmentFormValues) => {
      // TODO: Validate all fields are filled when fields component is implemented
      // TODO: Validate rules when rules component is implemented
      const data = {
        ...values,
        customTemplate: useTemplate ? values.customTemplate : null,
        customResults: useResults ? values.customResults : null,
        thankYouPage: useThankYou ? values.thankYouPage : null,
      } as Partial<Assessment>;
      return assessmentId
        ? updateAssessment({
            assessmentId: assessmentId,
            data: data,
          })
        : createAssessment({
            data: data,
          });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      if (assessmentId) {
        queryClient.invalidateQueries({
          queryKey: ['assessment', assessmentId],
        });
      }
      if (session?.id) {
        queryClient.invalidateQueries({
          queryKey: ['session'],
        });
      }
      toast.success(t('successMessage'));
      if (fromCalendar) {
        navigate(-1);
      }
    },
    onError: (error: any) => {
      toast.error(api.formatError(error, t('unableToSave')));
    },
  });

  if (isLoadingAssessment) {
    return <StickyCardSkeleton />;
  }

  // Helper function to parse formula (similar to Ember's parseFormula)
  const parseFormula = (formula: any, testFormula: boolean = false): string => {
    if (formula.advanced_input && formula.advanced_formula) {
      return formula.advanced_formula;
    }
    if (!formula.lhs || !Array.isArray(formula.lhs)) {
      return '';
    }
    return formula.lhs
      .map((operator: any) => {
        if (testFormula && operator.type === 'question') {
          return '1';
        }
        return operator.id || '';
      })
      .join(' ');
  };

  // Helper function to validate email addresses
  const validateEmails = (emails: string): boolean => {
    if (!emails) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cleanedEmails = emails
      .replace(/,,/g, ',')
      .replace(/\s|^,*|,*$/g, '')
      .split(',')
      .filter((v) => !!v);
    return cleanedEmails.every((email) => {
      const cleaned = email.replace(/(<|>| )/g, '').toLowerCase();
      return emailRegex.test(cleaned);
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('nameRequired')),
    fields: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string(),
          required: Yup.boolean(),
          type: Yup.string(),
          exercise: Yup.number().nullable(),
          options: Yup.array().of(
            Yup.object().shape({
              label: Yup.string(),
              points: Yup.number(),
              default: Yup.boolean(),
            })
          ),
        })
      )
      .test(
        'required-fields-have-labels',
        t('pleaseFillInAllAssessmentFields'),
        (fields) => {
          if (!fields) return true;
          return fields.every((field) => {
            return (
              (field.type === 'rep_max' && field.exercise) ||
              (field.label && (!field.options || field.options.every((o) => o)))
            );
          });
        }
      ),
    rules: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string(),
          formulas: Yup.array(),
          result: Yup.object().shape({
            type: Yup.string(),
            id: Yup.mixed().nullable(),
            data: Yup.mixed().nullable(),
          }),
        })
      )
      .test('validate-rules', function (rules) {
        if (!rules || rules.length === 0) return true;

        const errors: string[] = [];

        rules.forEach((rule: any, ruleIndex: number) => {
          const ruleName = rule.name || `Rule ${ruleIndex + 1}`;
          const formulas = rule.formulas || [];

          // Validate existing formula validity (matches Ember order)
          formulas.forEach((formula: any, formulaIndex: number) => {
            // Check if formula lhs is empty
            if (
              !formula.lhs ||
              !Array.isArray(formula.lhs) ||
              formula.lhs.length === 0
            ) {
              errors.push(
                t('ruleFormulaEmpty', {
                  index: formulaIndex + 1,
                  ruleName,
                })
              );
            } else {
              // Try to parse and validate formula syntax
              try {
                const formulaString = parseFormula(formula, true);
                // Note: Full syntax validation with expr-eval would require:
                // const expr = exprEval.Parser.parse(formulaString);
                // expr.evaluate();
                // For now, we just check if the formula string is non-empty
                if (!formulaString || formulaString.trim().length === 0) {
                  errors.push(
                    t('ruleFormulaEmpty', {
                      index: formulaIndex + 1,
                      ruleName,
                    })
                  );
                }
              } catch (err: any) {
                errors.push(
                  t('ruleFormulaInvalid', {
                    index: formulaIndex + 1,
                    ruleName,
                    error: err.message || String(err),
                  })
                );
              }
            }
          });

          // Validate missing result data
          const result = rule.result || {};
          if (!result.type || (!result.id && !result.data)) {
            errors.push(t('ruleAddAction', { ruleName }));
          }

          // Validate "email" result type
          if (result.type === 'email') {
            if (!result.id) {
              errors.push(t('ruleEnterEmail', { ruleName }));
            } else {
              if (!validateEmails(String(result.id))) {
                errors.push(t('ruleEnterValidEmail', { ruleName }));
              }
            }
          }

          // Validate missing formulas (check last, matches Ember order)
          if (formulas.length === 0) {
            errors.push(t('ruleAddFormula', { ruleName }));
          }
        });

        if (errors.length > 0) {
          return this.createError({
            path: this.path,
            message: errors.join('\n'),
          });
        }

        return true;
      }),
  });

  const initialValues: AssessmentFormValues = {
    id: assessmentData?.id || null,
    name: assessmentData?.name || '',
    description: assessmentData?.description || '',
    requiredAppText: assessmentData?.requiredAppText || '',
    scored: assessmentData?.scored || false,
    scoreFormula: assessmentData?.scoreFormula || '',
    addClientOnCompletion: assessmentData?.addClientOnCompletion || false,
    defaultLifecycleStageId: assessmentData?.defaultLifecycleStageId || null,
    unassignAfterCompletion: assessmentData?.unassignAfterCompletion || false,
    requireLogin: assessmentData?.requireLogin || false,
    sendEmailConfirmation: assessmentData?.sendEmailConfirmation || false,
    enableAssessementAnswerCheck:
      assessmentData?.enableAssessementAnswerCheck || false,
    makeCompletedPrivate: assessmentData?.makeCompletedPrivate || false,
    public: assessmentData?.public || false,
    autoAssignWeeks: assessmentData?.autoAssignWeeks ?? -1,
    buttonText: assessmentData?.buttonText || '',
    customCss: assessmentData?.customCss || '',
    customTemplate: assessmentData?.customTemplate || '',
    customResults: assessmentData?.customResults || '',
    thankYouPage: assessmentData?.thankYouPage || '',
    fields: assessmentData?.fields || [],
    rules: assessmentData?.rules || [],
  };

  const handleSave = async (values: FormikValues) => {
    saveMutation.mutate(values as AssessmentFormValues);
  };

  const handleBack = () => {
    if (fromCalendar) {
      navigate(-1);
    } else {
      navigate('/trainer/assessments', { replace: true });
    }
  };

  const currentTab = tabs.find((tab) => tab.value === curTab) ?? tabs[0];

  const pageTitle = assessmentId ? t('editAssessment') : t('newAssessment');

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="new-assessment"
        containerMaxWidth={'xl'}
        backButtonTitle={fromCalendar ? t('calendar') : t('createdAssessments')}
        backButtonLinkTo={handleBack}
        title={pageTitle}
      />
      <DebouncedFormik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({ submitForm }) => (
          <Form>
            <ScrollToFirstError offset={180} toastErrors={true} />
            <Stack direction={mdDown ? 'column' : 'row'}>
              <AdaptiveSideMenu
                navItems={tabs}
                activeValue={curTab}
                onValueChange={(value) => setCurTab(value)}
                collapsible={true}
                title={t('title')}
                id="new-assessment"
              />

              {/* Tab Content */}
              <StickyCard
                sx={{ width: '100%', mb: 3 }}
                title={currentTab.label}
                actions={
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={submitForm}
                    disabled={saveMutation.isPending}
                    loading={saveMutation.isPending}
                  >
                    {t('saveButton')}
                  </Button>
                }
              >
                {currentTab.component}
              </StickyCard>
            </Stack>
          </Form>
        )}
      </DebouncedFormik>
    </>
  );
};
